import { Box, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { updateCustomTotalWorkExperience, getResumesData } from 'api/users'
import { debounce } from 'lodash';
import dayjs from 'dayjs';

/**
 * TotalWorkExperienceDisplay
 * @description Component to display and edit the total work experience of a candidate
 * @prop {boolean} isResume - Indicates if the component is being used in a resume context
 * @prop {function} handleResumeProfile - Function to update the resume profile
 * @prop {array} workExperienceData - Array of work experience data
 * @prop {object} candidateData - Object containing candidate data
 * @returns {JSX.Element} A JSX element containing the total work experience input
 */
const TotalWorkExperienceDisplay = ({ isResume, handleResumeProfile, workExperienceData, candidateData }) => {
  const [experience, setExperience] = useState(candidateData?.custom_total_experience || 0);
  const [debouncer, setDebouncer] = useState("");
  const [isMaster, setIsMaster] = useState(false);

  useEffect(() => {
    fetchResume();
    const timer = setTimeout(() => {
      setDebouncer(experience);
    }, 500);
    return () => clearTimeout(timer);
  }, [experience, candidateData]);

  useEffect(() => {
    if (debouncer !== "") {
      /**
       * Updates the total work experience of the candidate.
       * If in resume context, updates the resume profile with the custom total experience.
       * Otherwise, it sends an update request to the server with the debounced total work experience.
       * Logs a success message upon completion or an error message if the update fails.
       */

      const updateExperience = async () => {
        try {
          if (isResume && experience !== debouncer) {
            handleResumeProfile({ custom_total_experience: debouncer });
            return;
          }
          await updateCustomTotalWorkExperience({ totalWorkExperience: debouncer });
          candidateData.custom_total_experience = debouncer;
          console.log("Updated successfully:", debouncer);
        } catch (error) {
          console.error("Error updating experience:", error);
        }
      };
      updateExperience();
    }
  }, [debouncer]);

  useEffect(() => {
    calculateExperience();
  }, [workExperienceData, isMaster]);

  /**
   * Fetches all resumes for the current user and checks if a Master Resume exists.
   * If a Master Resume is found, sets the isMaster state to true.
   * Logs an error message if the fetch fails.
   */
  const fetchResume = async () => {
    try {
      const response = await getResumesData();
      if (response?.success && response.data && response.data.length > 0) {
        const resumes = response.data;
        const masterResume = resumes.find((resume) => resume.title === 'Master Resume');
        if (masterResume) setIsMaster(true);
      }
    } catch (error) {
      console.error('Error fetching resumes:', error);
    }
  };

  /**
   * Calculates the total work experience based on the provided work experience data.
   * If the computed total experience is greater than the current state or if a Master Resume is present,
   * updates the experience state with the computed total.
   */

  const calculateExperience = () => {
    if (workExperienceData && workExperienceData.length > 0) {

      const { totalExperience } = calculateTotalExperience(workExperienceData);

      const total = totalExperience;
      // Only calculate and update experience if Master Resume exists
      if (isMaster) setExperience(total);
    }
  };

  /**
   * Handles changes to the total work experience input field.
   * Checks the value of the input field matches the regex pattern:
   * - empty string
   * - up to 2 digits before decimal
   * - only 1 digit after decimal
   * If the value matches the pattern, updates the experience state with the new value.
   */
  const handleChange = (e) => {
    const value = e.target.value;

    // Allow empty string, up to 2 digits before decimal, and only 1 digit after
    if (value === '' || /^\d{0,2}(\.\d{0,1})?$/.test(value)) {
      setExperience(value);
    }
  };

  /**
   * Calculates the total work experience in years based on the provided work history.
   * Returns an object with a single key: totalExperience, which is the total work experience in years.
   * If there is no valid work history, returns an object with totalExperience set to 0.
   * @param {Array<Object>} workHistory - Array of work experience entries. Each entry should have an "organization_name" key that is not equal to "Work Gap".
   * @returns {Object} - An object with a single key: totalExperience, which is the total work experience in years.
   */
  const calculateTotalExperience = (workHistory) => {
    const work = workHistory?.filter((work) => work?.organization_name !== 'Work Gap');
    if (work?.length > 0) {

      const workGaps = workHistory?.filter((work) => work?.organization_name == 'Work Gap');
      const totalGapDays = workGaps.reduce((total, gap) => {
        const start = new Date(gap.work_history_from);
        const end = gap.endDate === 'present' ? today : new Date(gap.work_history_to);
        const gapDays = (end - start) / (1000 * 60 * 60 * 24); // Convert ms to days
        return total + gapDays;
      }, 0);

      const today = new Date();
      let allMonths = new Set();
      work.forEach(job => {
        const startDate = dayjs(job.work_history_from);
        const endDate = job.present ? dayjs() : dayjs(job.work_history_to);

        // Add all months within the job period to the Set
        let current = startDate.startOf('month');
        while (current.isBefore(endDate.startOf('month')) || current.isSame(endDate.startOf('month'))) {
          allMonths.add(current.format("YYYY-MM"));
          current = current.add(1, 'month');
        }
      });

      const totalDurationYears = allMonths.size / 12;
      return { totalExperience: parseFloat(totalDurationYears.toFixed(1)) };
    }
    return { totalExperience: 0 };

  };

  return (
    <Box className="total-work-exp">
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems="center" spacing={1}>
        <Box className="count-years">
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <input
              type="text"
              value={experience}
              onChange={handleChange}
              inputMode="decimal"
              readOnly={isMaster}
              pattern="\d{1,2}(\.\d{1})?"
              maxLength={4} // e.g., "99.9"
              style={{
                width: '50px',
                textAlign: 'center',
                fontSize: '16px',
                background: 'transparent',
                border: 'none',
                outline: 'none',
                padding: 0,
                color: '#2B6CA3',
                fontWeight: 'bold',
              }}
            />
            <Typography className="year-txt">Years</Typography>
          </Stack>
        </Box>
        <Typography className="info-txt">
          is your total work experience (please update if needed)
        </Typography>
      </Stack>
    </Box>
  );
};

export default TotalWorkExperienceDisplay;
